// Shared inline-attachment renderer. Used by:
//   - AssistChatBubble (Customer Assist chat messages)
//   - NotesPanel       (internal team notes, migration 050)
//
// Two visual treatments based on MIME:
//   - image/* → inline preview with click-to-open + filename strip
//   - everything else → file pill with download icon + size
//
// Kept deliberately small and prop-driven — the same shape works for
// any "file attached to a message-like surface" with a public URL.

import { Download, Maximize2 } from 'lucide-react';
import { formatBytes, isInlineImage } from '@/lib/assist-attachments';
import { useLightbox } from '@/components/Lightbox';

/** Shared attachment shape. Mirrors what
 *  `customer_assist_messages.attachments` (migration 048) and
 *  `ticket_notes.attachments` (migration 050) both store as jsonb.
 *  Structurally compatible with `AttachmentMeta` from
 *  `use-assist-channel.ts` so the existing assist code paths pass
 *  through without conversion. */
export interface AttachmentLike {
  url: string;
  filename: string;
  contentType: string;
  size: number;
}

export function AttachmentTile({ att }: { att: AttachmentLike }) {
  const lightbox = useLightbox();

  if (isInlineImage(att)) {
    // Image attachments open in the app's shared <Lightbox> overlay
    // instead of a new browser tab. Keeps the user in the thread; the
    // overlay carries Download + Open-original + Close affordances so
    // every prior new-tab path is still reachable in one extra click.
    return (
      <button
        type="button"
        onClick={() => lightbox.open(att.url, att.filename, att.filename)}
        className="group block w-full overflow-hidden rounded-md border border-border/60 bg-background text-left"
      >
        <img
          src={att.url}
          alt={att.filename}
          loading="lazy"
          className="max-h-64 w-auto max-w-full object-contain"
        />
        <div className="flex items-center justify-between gap-2 border-t border-border/40 bg-background/80 px-2 py-1 text-3xs text-muted-foreground">
          <span className="truncate" title={att.filename}>{att.filename}</span>
          <span className="flex shrink-0 items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100">
            <Maximize2 className="h-3 w-3" />
            View
          </span>
        </div>
      </button>
    );
  }

  return (
    <a
      href={att.url}
      target="_blank"
      rel="noopener noreferrer"
      download={att.filename}
      className="flex items-center gap-2 rounded-md border border-border/60 bg-background px-2 py-1.5 text-xs hover:bg-accent"
    >
      <Download className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
      <span className="min-w-0 flex-1 truncate" title={att.filename}>{att.filename}</span>
      <span className="shrink-0 text-3xs text-muted-foreground">{formatBytes(att.size)}</span>
    </a>
  );
}
